import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { FinancialConvo } from "models/FinancialConvo";
import { OUTCOMES } from "../../../constants";
import {
  SingleSelectButtonForm,
  SelectButton,
} from "../shared/SingleSelectButtonForm";
import { WorriedIcon, OkIcon, SmileIcon } from "../shared/EmotionIcons";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  notSoGreat = "Not so great",
  ok = "Ok",
  iveGotThis = "I've got this",
}

const iconStyles = { width: 60, height: 60, padding: "6px" } as const;
const iconViewBox = "0 0 48 48";

const icons: { [key: string]: JSX.Element } = {
  notSoGreat: <WorriedIcon sx={iconStyles} viewBox={iconViewBox} />,
  ok: <OkIcon sx={iconStyles} viewBox={iconViewBox} />,
  iveGotThis: <SmileIcon sx={iconStyles} viewBox={iconViewBox} />,
};

const responses: { [key: string]: string } = {
  notSoGreat:
    "The good news is that we are here to help. We assure you that by talking to one of our advisors, you will feel more confident.",
  ok: "We understand! This stuff is complicated and that's why we are here ready to help. We are proud of you.",
  iveGotThis:
    "We’re proud of you for taking this in stride. This stuff is really difficult and we’ll be happy to assist you.",
};

const outcomesDictionary = enumToStringDictionary(Outcomes);
const feelingSelectButtons: SelectButton[] = [];
Object.keys(outcomesDictionary).forEach((key: string) =>
  feelingSelectButtons.push({
    key,
    text: outcomesDictionary[key],
    icon: icons[key],
    response: responses[key],
  })
);

export const FinancialFeelingsScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow } = useAuthContext()
  const { latestFinancialConvo, setLatestFinancialConvo } = useContext(
    FinancialConvoContext
  );
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (
    financialFeelings: string
  ): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    const financialConvo = latestFinancialConvo || new FinancialConvo();
    financialConvo.financialFeelings = financialFeelings;

    try {
      await financialConvo.saveRequest();
      setLatestFinancialConvo && setLatestFinancialConvo(financialConvo);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper title="How are you feeling about the financial side of settling the estate and inheriting/distributing different types of assets?">
      <SingleSelectButtonForm
        initChecked={latestFinancialConvo?.financialFeelings}
        selectButtons={feelingSelectButtons}
        required
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
